import { useToast } from "@hightouch/htui";
import {
  useDeleteOpportunityCardMutation,
  useUpdateOpportunityCardStatusMutation,
} from "src/graphql";
import { useOriginWithWorkspace } from "src/router";

export type OpportunityCardStatus = "open" | "saved";

export function getOpportunityDetailPath(cardId: string): string {
  return `/ad-studio/insights/opportunities/${cardId}`;
}

/**
 * Status updates, deletion, and share-link copying for an opportunity card,
 * shared by the feed card and the detail page. Mutations invalidate active
 * queries globally, so callers don't need to refetch the feed after a change.
 */
export function useOpportunityCardActions(cardId: string) {
  const { toast } = useToast();
  const originWithWorkspace = useOriginWithWorkspace();
  const statusMutation = useUpdateOpportunityCardStatusMutation();
  const deleteMutation = useDeleteOpportunityCardMutation();

  const setStatus = async (status: OpportunityCardStatus): Promise<boolean> => {
    try {
      await statusMutation.mutateAsync({ id: cardId, status });
      return true;
    } catch (error) {
      toast({
        id: `opportunity-status-error-${cardId}`,
        title: "Could not update opportunity",
        message: error instanceof Error ? error.message : "Try again.",
        variant: "error",
      });
      return false;
    }
  };

  const deleteCard = async (): Promise<boolean> => {
    try {
      await deleteMutation.mutateAsync({
        id: cardId,
        deletedAt: new Date().toISOString(),
      });
      return true;
    } catch (error) {
      toast({
        id: `opportunity-delete-error-${cardId}`,
        title: "Could not delete opportunity",
        message: error instanceof Error ? error.message : "Try again.",
        variant: "error",
      });
      return false;
    }
  };

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(
        `${originWithWorkspace}${getOpportunityDetailPath(cardId)}`,
      );
      toast({
        id: `opportunity-share-${cardId}`,
        title: "Link copied",
        variant: "success",
      });
    } catch (error) {
      toast({
        id: `opportunity-share-error-${cardId}`,
        title: "Could not copy link",
        message: error instanceof Error ? error.message : "Try again.",
        variant: "error",
      });
    }
  };

  return {
    setStatus,
    deleteCard,
    copyShareLink,
    isUpdatingStatus: statusMutation.isLoading,
    isDeleting: deleteMutation.isLoading,
  };
}
